import React, {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from 'react';
import {
  Box,
  createSvgIcon,
  IconButton,
  InputBase,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';
import AttachFileOutlinedIcon from '@mui/icons-material/AttachFileOutlined';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import InsertDriveFileOutlinedIcon from '@mui/icons-material/InsertDriveFileOutlined';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';

import PinguComposerChip from './PinguComposerChip';
import focusRing from '../../theme/focus';

const StopOutlinedSquareIcon = createSvgIcon(
  <rect x="6" y="6" width="12" height="12" rx="1.5" fill="none" stroke="currentColor" strokeWidth="2" />,
  'StopOutlinedSquare',
);

const MAX_MESSAGE_LENGTH = 20_000;
const MAX_FILE_COUNT = 5;
const MAX_TOTAL_FILE_SIZE = 15 * 1024 * 1024;
const MAX_PDF_SIZE = 10 * 1024 * 1024;
const MAX_IMAGE_SIZE = 3_750_000;
const SUPPORTED_FILE_TYPES = new Set([
  'application/pdf', 'image/gif', 'image/jpeg', 'image/png', 'image/webp',
]);
const MESSAGE_TOO_LONG_ERROR = 'Input is too large. Please decrease the size of input.';
const ATTACHMENT_ERROR_ID = 'pingwin-composer-attachment-error';
const ERROR_ID = 'pingwin-composer-error';

const controlSx = {
  width: 28,
  height: 28,
  borderRadius: 1.5,
  '&:focus-visible': { boxShadow: focusRing },
};

export type PinguComposerHandle = { focus: () => void };

type PinguComposerProps = {
  disabled: boolean;
  canCancel: boolean;
  contextChip?: React.ReactNode;
  onSend: (text: string, files: File[]) => void;
  onCancel: () => void;
  creditsMeter?: React.ReactNode;
  allowAttachments?: boolean;
};

const PinguComposer = forwardRef<PinguComposerHandle, PinguComposerProps>(({
  disabled,
  canCancel,
  contextChip,
  onCancel,
  onSend,
  creditsMeter,
  allowAttachments = true,
}, ref) => {
  const [input, setInput] = useState('');
  const [files, setFiles] = useState<File[]>([]);
  const [attachmentError, setAttachmentError] = useState('');
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const stopRef = useRef<HTMLButtonElement>(null);
  const focusPending = useRef(false);
  const tooLong = input.length > MAX_MESSAGE_LENGTH;
  const canSend = !disabled && (Boolean(input.trim()) || files.length > 0) && !tooLong;

  const selectFiles = (event: React.ChangeEvent<HTMLInputElement>) => {
    const fileInput = event.currentTarget;
    const selected = Array.from(fileInput.files || []);
    fileInput.value = '';
    const combined = [...files, ...selected];
    const supported = selected.every((file) => SUPPORTED_FILE_TYPES.has(file.type)
      && file.size <= (file.type === 'application/pdf' ? MAX_PDF_SIZE : MAX_IMAGE_SIZE));
    const totalSize = combined.reduce((total, file) => total + file.size, 0);
    if (!supported) {
      setAttachmentError('Use PDF, JPEG, PNG, GIF, or WebP files within the supported size limit.');
      return;
    }
    if (combined.length > MAX_FILE_COUNT) {
      setAttachmentError(`Attach up to ${MAX_FILE_COUNT} files per message.`);
      return;
    }
    if (totalSize > MAX_TOTAL_FILE_SIZE) {
      setAttachmentError('Attachments must total no more than 15 MB.');
      return;
    }
    setFiles(combined);
    setAttachmentError('');
  };

  const removeFile = (indexToRemove: number) => {
    setFiles((previous) => previous.filter((_, index) => index !== indexToRemove));
    setAttachmentError('');
  };

  // The textarea is disabled while a reply is pending, and stop is then the live control.
  const focusLiveControl = (): boolean => {
    const textarea = textareaRef.current;
    if (textarea && !textarea.disabled) {
      textarea.focus();
      return true;
    }
    if (stopRef.current) {
      stopRef.current.focus();
      return true;
    }
    return false;
  };

  useImperativeHandle(ref, () => ({
    focus: () => { focusPending.current = !focusLiveControl(); },
  }), []);

  // Loading a conversation disables the textarea before any stop control exists,
  // so a focus request made then is kept until one of them is live.
  useEffect(() => {
    if (focusPending.current && focusLiveControl()) focusPending.current = false;
  }, [disabled, canCancel]);

  // The card is larger than the textarea, so a press on its empty space puts the caret there.
  // Only the wrappers marked data-composer-empty count: controls, file names, the span around
  // a disabled button and anything portaled out of the card (tooltips) keep their default.
  // The space below the text is the InputBase root, whose own click handler focuses the textarea.
  // preventDefault keeps focus from leaving the textarea between press and release.
  const focusFromCard = (event: React.MouseEvent<HTMLElement>) => {
    const textarea = textareaRef.current;
    if (event.button !== 0 || !textarea || textarea.disabled) return;
    if (!(event.target instanceof HTMLElement) || !event.target.hasAttribute('data-composer-empty')) return;
    event.preventDefault();
    textarea.focus();
  };

  const submit = () => {
    if (!canSend) return;
    const text = input.trim();
    setInput('');
    setFiles([]);
    onSend(text, files);
  };

  return (
    <Box sx={{ px: 4, py: 3 }}>
      <Box
        onMouseDown={focusFromCard}
        data-composer-empty
        sx={{
          display: 'flex',
          flexDirection: 'column',
          border: 1,
          borderColor: tooLong ? 'error.main' : 'divider',
          borderRadius: 1.5,
          backgroundColor: disabled ? 'background.default' : 'background.paper',
          '&:focus-within': {
            borderColor: tooLong ? 'error.main' : 'emphasisText.main',
          },
          position: 'relative',
        }}
      >
        {(contextChip || files.length > 0) && (
          // One wrapping row for the page tag and the files, its first chip in line with the text.
          <Box
            data-composer-empty
            sx={{
              display: 'flex',
              flexWrap: 'wrap',
              gap: 1.5,
              px: 3,
              pt: 2,
            }}
          >
            {contextChip}
            {files.map((file, index) => (
              <PinguComposerChip
                key={`${file.name}-${file.lastModified}-${file.size}`}
                icon={<InsertDriveFileOutlinedIcon />}
                label={file.name}
                title={file.name}
                aria-label={`Remove ${file.name}`}
                // MUI only disables clickable chips, so a chip with just onDelete stays focusable
                // and still deletes on Backspace and Delete. Skip focus and guard both handlers.
                onDelete={() => {
                  if (!disabled) removeFile(index);
                }}
                // The chip is named for its one action, so Enter and Space remove it too.
                onKeyDown={(event) => {
                  if (disabled || event.target !== event.currentTarget) return;
                  if (event.key !== 'Enter' && event.key !== ' ') return;
                  event.preventDefault();
                  removeFile(index);
                }}
                disabled={disabled}
                skipFocusWhenDisabled
                sx={{ '& .MuiChip-label': { maxWidth: 220 } }}
              />
            ))}
          </Box>
        )}
        <InputBase
          inputRef={textareaRef}
          multiline
          minRows={2}
          maxRows={6}
          value={input}
          onChange={(event) => setInput(event.target.value)}
          onKeyDown={(event) => {
            if (event.key === 'Enter' && !event.shiftKey) {
              event.preventDefault();
              submit();
            }
          }}
          disabled={disabled}
          placeholder="Ask Pingwin…"
          inputProps={{
            'aria-label': 'Ask Pingwin',
            'aria-invalid': tooLong,
            'aria-describedby': tooLong ? ERROR_ID : undefined,
          }}
          sx={{
            typography: 'body1',
            p: 3,
            // The 36px button row (8px inset plus a 28px button) and 12px of room above it.
            paddingBottom: 12,
            '& textarea': { overflowY: 'auto' },
            '& .MuiInputBase-input::placeholder': { color: 'text.secondary', opacity: 1 },
          }}
        />
        {allowAttachments && (
          <input
            ref={fileInputRef}
            type="file"
            hidden
            multiple
            accept="application/pdf,image/gif,image/jpeg,image/png,image/webp"
            onChange={selectFiles}
          />
        )}
        <Box
          data-composer-empty
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            position: 'absolute',
            bottom: 8,
            left: 8,
            right: 8,
          }}
        >
          {allowAttachments ? (
            <Tooltip title="Attach files">
              <span>
                <IconButton
                  aria-label="Attach files"
                  disabled={disabled || files.length >= MAX_FILE_COUNT}
                  onClick={() => fileInputRef.current?.click()}
                  // The paperclip glyph starts 7 units into its 24-unit viewBox, about 11px into
                  // the button, so pulling it 7px left lines the glyph up with the text at 12px.
                  sx={{ ...controlSx, ml: '-7px', color: 'text.secondary' }}
                >
                  <AttachFileOutlinedIcon sx={{ fontSize: 16 }} />
                </IconButton>
              </span>
            </Tooltip>
          ) : <span />}
          {canCancel ? (
            <Tooltip title="Stop generating">
              <IconButton
                ref={stopRef}
                onClick={onCancel}
                aria-label="Stop generating"
                sx={{
                  ...controlSx,
                  border: 1,
                  borderColor: 'divider',
                  backgroundColor: 'background.paper',
                  color: 'text.secondary',
                }}
              >
                <StopOutlinedSquareIcon sx={{ fontSize: 16 }} />
              </IconButton>
            </Tooltip>
          ) : (
            <Tooltip title="Send">
              <span>
                <IconButton
                  onClick={submit}
                  aria-label="Send"
                  disabled={!canSend}
                  sx={{
                    ...controlSx,
                    backgroundColor: 'secondary.main',
                    color: 'secondary.contrastText',
                    '&:hover': { backgroundColor: 'secondary.dark' },
                    '&.Mui-disabled': {
                      backgroundColor: 'action.disabledBackground',
                      color: 'action.disabled',
                    },
                  }}
                >
                  <ArrowUpwardIcon sx={{ fontSize: 16 }} />
                </IconButton>
              </span>
            </Tooltip>
          )}
        </Box>
      </Box>
      {tooLong && (
        <Typography id={ERROR_ID} variant="body1" color="error" sx={{ mt: 1 }}>
          {MESSAGE_TOO_LONG_ERROR}
        </Typography>
      )}
      {attachmentError && (
        <Typography id={ATTACHMENT_ERROR_ID} variant="body1" color="error" sx={{ mt: 1 }}>
          {attachmentError}
        </Typography>
      )}
      <Stack spacing={1} alignItems="center" sx={{ mt: 2, textAlign: 'center' }}>
        <Box sx={{ position: 'relative', ...(creditsMeter && { alignSelf: 'stretch', px: 7 }) }}>
          <Typography variant="footnote" color="text.secondary" component="p">
            Pingwin is AI and can make mistakes. Review answers.
          </Typography>
          {creditsMeter && (
            <Box
              sx={{
                position: 'absolute',
                right: 0,
                top: '50%',
                transform: 'translateY(-50%)',
                display: 'flex',
              }}
            >
              {creditsMeter}
            </Box>
          )}
        </Box>
        <Stack direction="row" spacing={1} alignItems="center" justifyContent="center">
          <LockOutlinedIcon aria-hidden sx={{ fontSize: 14, color: 'text.secondary', flexShrink: 0 }} />
          <Typography variant="footnote" color="text.secondary" component="p">
            Your data is never used for model training.
          </Typography>
        </Stack>
      </Stack>
    </Box>
  );
});

PinguComposer.displayName = 'PinguComposer';

export default PinguComposer;
